import { CheckCircle2 } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Badge } from '@/components/ui/badge'
import { Separator } from '@/components/ui/separator'
import { ScrollArea } from '@/components/ui/scroll-area'
import type { StructuredProfile } from '@/features/charter/redux/types/charterTypes'
import {
  formatProfileKey as formatKey,
  renderProfileValue as renderValue,
} from '@/features/charter/utils/profileFormat'

interface PrepProfileModalProps {
  isOpen: boolean
  onClose: () => void
  profile: StructuredProfile
  profileApproved: boolean
}

export function PrepProfileModal({
  isOpen,
  onClose,
  profile,
  profileApproved,
}: PrepProfileModalProps) {
  const individualEntries = Object.entries(profile.individual_content ?? {})
  const negotiationEntries = Object.entries(profile.negotiation_positions ?? {})

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className='sm:max-w-lg p-0 gap-0 overflow-hidden'>
        <DialogHeader className='px-6 pt-6 pb-4'>
          <div className='flex items-center justify-between'>
            <DialogTitle className='text-base font-semibold text-slate-900'>
              Your Prep Profile
            </DialogTitle>
            <Badge
              variant='outline'
              className={
                profileApproved
                  ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                  : 'bg-amber-50 text-amber-700 border-amber-200'
              }
            >
              {profileApproved ? 'Approved' : 'Draft'}
            </Badge>
          </div>
        </DialogHeader>

        <ScrollArea className='max-h-[70vh] px-6 pb-6'>
          <div className='space-y-3'>
            {individualEntries.map(([key, value]) => (
              <div key={key}>
                <p className='text-[10px] font-semibold uppercase tracking-wide text-gray-400 mb-0.5'>
                  {formatKey(key)}
                </p>
                <p className='text-sm text-gray-700 leading-relaxed'>
                  {renderValue(value)}
                </p>
              </div>
            ))}

            {individualEntries.length > 0 && negotiationEntries.length > 0 && (
              <Separator className='my-1' />
            )}

            {negotiationEntries.map(([key, value]) => (
              <div key={key}>
                <p className='text-[10px] font-semibold uppercase tracking-wide text-gray-400 mb-0.5'>
                  {formatKey(key)}
                </p>
                <p className='text-sm text-gray-700 leading-relaxed'>
                  {renderValue(value)}
                </p>
              </div>
            ))}

            {profile.shareable_summary && (
              <>
                <Separator className='my-1' />
                <div>
                  <p className='text-[10px] font-semibold uppercase tracking-wide text-gray-400 mb-0.5'>
                    Summary
                  </p>
                  <p className='text-sm text-gray-700 leading-relaxed'>
                    {profile.shareable_summary}
                  </p>
                </div>
              </>
            )}

            {profileApproved && (
              <div className='flex items-center gap-2 p-2.5 mt-2 bg-emerald-50 rounded-lg border border-emerald-200 text-xs text-emerald-700'>
                <CheckCircle2 className='h-3.5 w-3.5 flex-shrink-0' />
                Profile approved — waiting for teammates to complete their prep.
              </div>
            )}
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  )
}
